import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { PATHS, ASSETS } from "@/content/site";
import useMediaQuery from "@/hooks/useMediaQuery";

const COUNT = PATHS.length;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function CTA({ path }) {
  return (
    <Link
      to={path.ctaHref}
      data-testid={`path-cta-${path.slug}`}
      className="group mt-7 inline-flex items-center gap-2 bg-coral text-white px-6 py-3.5 text-[0.95rem] font-semibold hover:bg-[#d85a5e] transition-colors"
      style={{ backgroundColor: "var(--coral)" }}
    >
      {path.ctaLabel}
      <ArrowLeft size={17} className="transition-transform group-hover:-translate-x-1" />
    </Link>
  );
}

/* Active content block — atomic single-state unit (title + desc + cta) */
function ActiveContent({ path, reduced }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={path.slug}
        data-testid={`path-content-${path.slug}`}
        initial={reduced ? false : { opacity: 0, y: 14 }}
        animate={reduced ? {} : { opacity: 1, y: 0 }}
        exit={reduced ? {} : { opacity: 0, y: -10 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: "var(--peach)" }}>{path.title}</h2>
        <p className="mt-5 text-white/85 leading-relaxed text-lg max-w-xl">{path.description}</p>
        <CTA path={path} />
      </motion.div>
    </AnimatePresence>
  );
}

/* Shared scene image + side gradients + moving lens */
function SceneImage({ lensPosition, reduced }) {
  return (
    <>
      <img src={ASSETS.meeting} alt={PATHS[0].imageAlt} width="1280" height="720" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(40,62,85,0.92) 0%, rgba(40,62,85,0.35) 34%, rgba(40,62,85,0.30) 66%, rgba(40,62,85,0.92) 100%)" }} />
      <div
        className={`absolute w-[34rem] h-[34rem] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2 ${reduced ? "" : "transition-all duration-500 ease-out"}`}
        style={{ background: "radial-gradient(circle, rgba(234,173,140,0.45), transparent 68%)", left: lensPosition.split(" ")[0], top: lensPosition.split(" ")[1] }}
        aria-hidden="true"
      />
    </>
  );
}

function scrollToIndex(trackRef, i) {
  const el = trackRef.current;
  if (!el) return;
  const rectTop = el.offsetTop;
  const travel = el.offsetHeight - window.innerHeight;
  const target = rectTop + ((i + 0.5) / COUNT) * travel;
  window.scrollTo({ top: target, behavior: "smooth" });
}

/* ------------------------------- Desktop ------------------------------- */
function DesktopStory() {
  const trackRef = useRef(null);
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start start", "end end"] });
  const fillHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(COUNT - 1, Math.floor(clamp(v, 0, 0.999) * COUNT)));
  });
  const path = PATHS[active];

  return (
    <div ref={trackRef} style={{ height: "350svh" }} className="relative">
      <div className="sticky overflow-hidden" style={{ top: "var(--header-height)", height: "calc(100svh - var(--header-height))" }}>
        <SceneImage lensPosition={path.lensPosition} reduced={reduced} />

        <div className="relative h-full mx-auto max-w-[1400px] px-6 sm:px-10 grid grid-cols-[minmax(280px,360px)_1fr] gap-12 items-center">
          {/* Vertical Siaq line = list + progress */}
          <nav aria-label="مساراتنا في التأثير" className="relative self-center py-6">
            <span className="absolute top-2 bottom-2 w-px bg-white/20" style={{ insetInlineStart: "7px" }} aria-hidden="true" />
            <motion.span className="absolute top-2 w-[3px]" style={{ insetInlineStart: "6px", backgroundColor: "var(--coral)", height: fillHeight, maxHeight: "calc(100% - 16px)" }} aria-hidden="true" />
            <ul className="relative space-y-1">
              {PATHS.map((p, i) => {
                const on = i === active;
                return (
                  <li key={p.slug} className="relative">
                    <button
                      data-testid={`path-tab-${p.slug}`}
                      aria-current={on ? "true" : undefined}
                      onClick={() => scrollToIndex(trackRef, i)}
                      className="group flex items-center gap-4 w-full text-start py-2.5"
                    >
                      <span className="relative z-10 grid place-items-center shrink-0" style={{ width: 15, height: 15 }}>
                        <span className="rounded-full transition-all duration-500" style={{ width: on ? 15 : 9, height: on ? 15 : 9, backgroundColor: on ? "var(--coral)" : "rgba(255,255,255,0.45)", boxShadow: on ? "0 0 0 4px rgba(229,107,111,0.25)" : "none" }} />
                      </span>
                      <span className="text-[1.02rem] transition-colors duration-300" style={{ color: on ? "#fff" : "rgba(255,255,255,0.55)" }}>{p.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="relative">
            <span className="eyebrow mb-5">مساراتنا في التأثير</span>
            <ActiveContent path={path} reduced={reduced} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Mobile ------------------------------- */
function MobileStory() {
  const trackRef = useRef(null);
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start start", "end end"] });
  const fillW = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(COUNT - 1, Math.floor(clamp(v, 0, 0.999) * COUNT)));
  });
  const path = PATHS[active];

  return (
    <div ref={trackRef} style={{ height: `${COUNT * 60}svh` }} className="relative">
      <div className="sticky overflow-hidden" style={{ top: "var(--header-height)", height: "calc(100svh - var(--header-height))" }}>
        <SceneImage lensPosition={path.lensPosition} reduced={reduced} />
        {/* horizontal progress line */}
        <span className="absolute top-0 inset-x-0 h-[3px] bg-white/15" aria-hidden="true" />
        <motion.span className="absolute top-0 start-0 h-[3px]" style={{ width: fillW, backgroundColor: "var(--coral)" }} aria-hidden="true" />

        <div dir="rtl" className="absolute inset-x-0 bottom-0 p-6 text-right">
          <span className="eyebrow mb-3" style={{ color: "var(--peach)" }}>مساراتنا في التأثير</span>
          <ActiveContent path={path} reduced={reduced} />
        </div>
      </div>

      {/* Accessible list of all paths (does not require interaction to view the story) */}
      <nav aria-label="مساراتنا في التأثير" className="sr-only">
        {PATHS.map((p, i) => (
          <button key={p.slug} data-testid={`path-tab-${p.slug}`} onClick={() => scrollToIndex(trackRef, i)}>{p.title}</button>
        ))}
      </nav>
    </div>
  );
}

export default function ImpactPaths({ id = "paths" }) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  return (
    <section id={id} data-testid="impact-paths-section" className="bg-navy text-white" style={{ backgroundColor: "var(--navy)" }}>
      {isDesktop ? <DesktopStory /> : <MobileStory />}
    </section>
  );
}
